import { useMemo } from "react";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { formatBrazilDocument } from "@/shared/utils/brazilDocument";

import { brazilPayerSchema, type BrazilPayerValues } from "./depositPayer.schema";
import type { DepositPayerStepProps } from "./RailDepositDrawer";

/** The payer of a PIX collection: name and CPF/CNPJ, which the provider needs to mint the QR. */
export function BrazilPayerStep({
  formId,
  defaults,
  amount,
  onSubmit,
}: DepositPayerStepProps<BrazilPayerValues>) {
  const { t } = useTranslations("wallets");
  const schema = useMemo(
    () =>
      brazilPayerSchema({
        name: t("deposit_payer_name_error"),
        document: t("deposit_payer_doc_error"),
        documentCheck: t("deposit_payer_doc_check_error"),
      }),
    [t],
  );
  const form = useZodForm(schema, defaults);

  return (
    <ColocaForm id={formId} form={form} onSubmit={onSubmit} className="space-y-5">
      {amount}
      <div className="space-y-4">
        <ColocaInputField
          name="payerName"
          label={t("deposit_payer_name_label")}
          placeholder={t("deposit_payer_name_placeholder")}
          maxLength={120}
          autoComplete="name"
          required
        />
        <ColocaInputField
          name="document"
          label={t("deposit_payer_doc_label")}
          placeholder={t("deposit_payer_doc_placeholder")}
          description={t("deposit_payer_hint")}
          inputMode="numeric"
          maxLength={18}
          autoComplete="off"
          transform={formatBrazilDocument}
          required
        />
      </div>
    </ColocaForm>
  );
}
